import { ChangeEvent } from 'react'

export type InputEventHelper<T> = (
	param: T,
) => <
	E extends ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>,
>(
	event: E,
) => E

export const maxLength: InputEventHelper<number> = (maxLength) => (event) => {
	const target = event.target
	const value = target.value

	// If the value is already within the limit, do nothing.
	if (value.length <= maxLength) {
		return event
	}

	const cursorPosition = target.selectionStart

	target.value = value.slice(0, maxLength)

	// Restore the cursor position a moment later
	// We check the cursor position to avoid moving it if the user was typing
	// at the very end of the truncated string.
	if (cursorPosition && cursorPosition <= maxLength) {
		target.setSelectionRange(cursorPosition, cursorPosition)
	}

	return event
}

export const addPrefix: InputEventHelper<string> = (prefix) => (event) => {
	const { value } = event.target
	if (value === '') {
		event.target.value = ''
		return event
	}
	if (!value.startsWith(prefix)) {
		event.target.value = `${prefix}${value}`
	}
	return event
}

export const filterChars: InputEventHelper<string> =
	(allowedCharsRange: string) => (event) => {
		// Ensure the input is a valid regex character class
		if (
			!allowedCharsRange.startsWith('[') ||
			!allowedCharsRange.endsWith(']')
		) {
			throw new Error(
				'Invalid character range notation. Must be in format: [a-zA-Z0-9 -]',
			)
		}

		// Extract the character class without brackets
		const allowedChars = allowedCharsRange.slice(1, -1)

		// Create a dynamic regex to remove disallowed characters
		const filterRegex = new RegExp(`[^${allowedChars}]`, 'g')
		event.target.value = event.target.value.replace(filterRegex, '')
		return event
	}

/**
 * Removes emojis and emoji-related symbols from text
 * @param text - The input text to clean
 * @returns The text with all emojis removed
 */
export const removeEmojis = (text: string): string => {
	return text.replace(
		/[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{2B00}-\u{2BFF}]|[\u{1FA70}-\u{1FAFF}]|[\u{1F900}-\u{1F9FF}]|[\u{1F200}-\u{1F2FF}]|[\u{1F000}-\u{1F02F}]|[\u{203C}-\u{203C}]|[\u{2049}-\u{2049}]/gu,
		'',
	)
}
